import { useEffect, useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Title2 } from '@sorare/core/src/atoms/typography';
import Dialog from '@sorare/core/src/components/dialog';
import PlayNowButton from '@sorare/core/src/components/landing/LandingFootball/PlayNowButton';
import {
  DrukWide40,
  MarketingText20,
} from '@sorare/core/src/components/marketing/typography';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import useIsVisibleInViewport from '@sorare/core/src/hooks/ui/useIsVisibleInViewport';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

interface Props {
  text: string | null;
  cta: string | null;
  ctaUrl: string | null;
}

const messages = defineMessages({
  dialogTitle: {
    id: 'ScrollPopup.dialogTitle',
    defaultMessage: 'Want more?',
  },
});

const Wrapper = styled(Vertical).attrs({ gap: 3, center: true })`
  padding: var(--double-unit);
  justify-content: stretch;

  @media ${laptopAndAbove} {
    width: 480px;
  }
`;

const ScrollPopup = ({ text, cta, ctaUrl }: Props) => {
  const [showned, setShowned] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(true);
  const { handleRef, isVisible } = useIsVisibleInViewport<HTMLDivElement>();
  const { currentUser } = useCurrentUserContext();

  useEffect(() => {
    if (isVisible && !showned) {
      setShowned(true);
    }
  }, [isVisible, showned, setShowned]);

  if (!text || !cta || !ctaUrl || currentUser) return null;

  return (
    <div ref={handleRef}>
      {showned && (
        <Dialog
          maxWidth={false}
          open={open}
          onClose={() => setOpen(false)}
          title={
            <Title2>
              <DrukWide40>
                <FormattedMessage {...messages.dialogTitle} />
              </DrukWide40>
            </Title2>
          }
        >
          <Wrapper>
            <MarketingText20>{text}</MarketingText20>
            <PlayNowButton color="primary" to={ctaUrl} title={cta} />
          </Wrapper>
        </Dialog>
      )}
    </div>
  );
};

export default ScrollPopup;
